import {
  getWorkspaceUuidFromCurrentUrl,
  parseErrorMessages,
  updateSelectedTableViewInLS,
} from "src/utils/methods";
import {
  RESET_HIDDEN_COLUMNS,
  SET_DICTIONARY_COLUMN_RANK,
  SET_DICTIONARY_FILTER,
  SET_DICTIONARY_FILTERS,
  SET_DICTIONARY_INITIAL_COLUMN_RANKS,
  SET_DICTIONARY_SORT,
  SET_DICTIONARY_SORT_BY,
  SET_HIDDEN_DICTIONARY_COLUMN_IDS,
  SET_SELECTED_TABLE_VIEW,
  TOGGLE_DICTIONARY_COLUMN_VISIBILITY,
} from "src/app/components/DictionaryFilters/redux/dictionaryFiltersTypes";
import { valueExists } from "src/app/methods/valueExists";
import { generateRankString } from "src/utils/rankStrings";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "src/utils/localStorage";

const wsWorkspaceUuid = getWorkspaceUuidFromCurrentUrl();

export const generateRanksForNewMetaFields = (lastItemRank, columns) => {
  if (!lastItemRank || !columns.length) return [];
  let newRank = null;
  return (
    columns?.map((col) => {
      newRank = generateRankString(newRank || lastItemRank, "");
      return { uuid: col.uuid, rank: newRank };
    }) || []
  );
};

export const generateMetadataRanks = (columns) => {
  return columns?.map((col) => ({ uuid: col.uuid, rank: col.rank })) || [];
};

export const generateLackingRanks = (columns, ranks) => {
  if (!columns?.length && ranks?.length) return ranks;

  const sortedRanks = [];
  const newTasks = [];

  ranks
    ?.sort((a, b) => a.rank.localeCompare(b.rank))
    ?.forEach((rank) => {
      if (columns.find((item) => item.uuid === rank.uuid)) {
        sortedRanks.push(rank);
      }
    });

  columns?.forEach((col) => {
    if (!sortedRanks.find((item) => item.uuid === col.uuid)) {
      newTasks.push(col);
    }
  });

  const lastRank = sortedRanks[sortedRanks.length - 1]?.rank;

  const newRanks = [
    ...sortedRanks,
    ...generateRanksForNewMetaFields(lastRank, newTasks),
  ];

  return newRanks;
};

const defaultSelectedTableView = {
  label: "Custom",
  value: null,
};

export const creatorDatabaseFiltersReducerInitialState = (
  globalTaskColumns,
) => {
  const settingsData = getFromLocalStorage("ws-creator-database-settings");
  const settings = settingsData ? settingsData[wsWorkspaceUuid] : null;

  const selectedTableView = settings?.selectedTableView;

  if (selectedTableView?.data) {
    return {
      ...settings,
      ...selectedTableView.data,
      selectedTableView,
      creatorDatabaseColumnRanks: generateLackingRanks(
        globalTaskColumns,
        settings?.creatorDatabaseColumnRanks,
      ),
    };
  }

  if (settings?.creatorDatabaseColumnRanks) {
    const newSettings = {
      ...settings,
      creatorDatabaseColumnRanks: generateLackingRanks(
        globalTaskColumns,
        settings?.creatorDatabaseColumnRanks,
      ),
    };

    return newSettings;
  }

  const newColumnRanks = generateMetadataRanks(globalTaskColumns);

  if (settings) {
    return {
      ...settings,
      creatorDatabaseColumnRanks: newColumnRanks,
    };
  }

  return {
    sortBy: null,
    sort: "DESC",
    filters: [],
    hiddenColumns: [],
    creatorDatabaseColumnRanks: newColumnRanks,
    selectedTableView: defaultSelectedTableView,
  };
};

export const save = (obj, skipLSUpdate) => {
  const previousSave = getFromLocalStorage("ws-creator-database-settings");

  const itemToSet = previousSave?.hasOwnProperty(wsWorkspaceUuid)
    ? {
        ...previousSave[wsWorkspaceUuid],
        ...obj,
      }
    : obj;

  if (!skipLSUpdate) {
    saveToLocalStorage("ws-creator-database-settings", {
      ...previousSave,
      [wsWorkspaceUuid]: itemToSet,
    });
  }
  return sanitizeFilters(obj);
};

const sanitizeFilters = (obj) => {
  // stupid but it works, gets rid of keys for undefined values
  return JSON.parse(JSON.stringify(obj));
};

function creatorDatabaseFiltersReducer(state, action) {
  if (
    action?.payload?.data?.status === "fail" ||
    action?.error?.response?.status >= 500
  ) {
    return {
      ...state,
      errorMessages: [{ message: "An unexpected error occurred." }],
    };
  }

  switch (action.type) {
    case SET_DICTIONARY_SORT_BY:
      return save(
        {
          ...state,
          selectedTableView: defaultSelectedTableView,
          sortBy: action.payload.sortBy,
        },
        action.payload.skipLSUpdate,
      );

    case SET_DICTIONARY_SORT:
      return save(
        {
          ...state,
          selectedTableView: defaultSelectedTableView,
          sort: action.payload.sort,
        },
        action.payload.skipLSUpdate,
      );

    case SET_DICTIONARY_FILTERS:
      return save(
        {
          ...state,
          selectedTableView: defaultSelectedTableView,
          filters: action.payload.filters,
        },
        action.payload.skipLSUpdate,
      );

    case SET_DICTIONARY_FILTER:
      return save({
        ...state,
        selectedTableView: defaultSelectedTableView,
        filters: {
          ...state.filters,
          [action.payload.fieldUuid]: !valueExists(action.payload.value)
            ? undefined
            : { value: action.payload.value },
        },
      });

    case TOGGLE_DICTIONARY_COLUMN_VISIBILITY: {
      const hiddenColumns = state.hiddenColumns || [];

      return save({
        ...state,
        selectedTableView: defaultSelectedTableView,
        hiddenColumns: hiddenColumns?.includes(action.payload.uuid)
          ? hiddenColumns?.filter((hc) => hc !== action.payload.uuid)
          : [...hiddenColumns, action.payload.uuid],
      });
    }

    case SET_HIDDEN_DICTIONARY_COLUMN_IDS:
      return save(
        {
          ...state,
          selectedTableView: defaultSelectedTableView,
          hiddenColumns: action.payload.ids,
        },
        action.payload.skipLSUpdate,
      );

    case SET_DICTIONARY_INITIAL_COLUMN_RANKS:
      return {
        ...state,
        creatorDatabaseColumnRanks: action.payload.ranks,
      };

    case RESET_HIDDEN_COLUMNS:
      return save({
        ...state,
        selectedTableView: defaultSelectedTableView,
        hiddenColumns: [],
      });

    case SET_DICTIONARY_COLUMN_RANK: {
      const { uuid, rank } = action.payload;
      return save({
        ...state,
        selectedTableView: defaultSelectedTableView,
        creatorDatabaseColumnRanks: state.creatorDatabaseColumnRanks.map(
          (col) => {
            if (col.uuid === uuid) {
              return {
                ...col,
                rank,
              };
            }

            return col;
          },
        ),
      });
    }

    case SET_SELECTED_TABLE_VIEW:
      updateSelectedTableViewInLS(
        "ws-creator-database-settings",
        action.payload.selectedTableView,
      );
      return {
        ...state,
        selectedTableView: action.payload.selectedTableView,
      };

    default:
      if (action?.error?.response?.data && action.type.includes("_FAIL")) {
        return {
          ...state,
          errorMessages: parseErrorMessages(
            action?.error?.response?.data?.errors,
          ),
        };
      }

      return state;
  }
}

export default creatorDatabaseFiltersReducer;
